import React, { useState, useRef, useEffect } from 'react';
import { X, Mail, Users, User, Send, AlertCircle, CheckCircle, RefreshCw, Bold, Italic, Underline, List, AlignLeft, AlignCenter, AlignRight, Link, Type } from 'lucide-react';
import Button from '../../../components/ui/Button';

// Default onboarding email template
const DEFAULT_SUBJECT = 'Welcome to Fortified Mind — Your Scripture Journey Begins!';

const DEFAULT_HTML = `<h2>Welcome to Fortified Mind!</h2>
<p>We're so glad you've joined our community of Scripture memorizers. Fortified Mind is designed to help you hide God's Word in your heart through proven memorization techniques and daily review.</p>
<h3>Getting Started</h3>
<ul>
  <li><strong>Add your first verse</strong> — Head to your Verse Library and add a passage you want to memorize.</li>
  <li><strong>Start your daily review</strong> — Each day, review your verses using our spaced repetition system.</li>
  <li><strong>Track your progress</strong> — Watch your streak grow and celebrate milestones along the way.</li>
</ul>
<p>If you have any questions, feel free to reach out to our support team. We're here to help you succeed!</p>
<p>Blessings,<br/><strong>The Fortified Mind Team</strong></p>
<hr/>
<p style="font-size:12px;color:#9ca3af;">"None but those who have fortified the mind with the truths of the Bible will stand through the last great conflict."</p>`;

// Simple rich text editor using contentEditable
const RichTextEditor = ({ value, onChange }) => {
  const editorRef = useRef(null);
  const [activeFormats, setActiveFormats] = useState({});

  useEffect(() => {
    if (editorRef?.current && editorRef?.current?.innerHTML !== value) {
      editorRef.current.innerHTML = value;
    }
  }, []);

  const execCmd = (command, val = null) => {
    editorRef?.current?.focus();
    document.execCommand(command, false, val);
    updateActiveFormats();
    if (onChange) onChange(editorRef?.current?.innerHTML || '');
  };

  const updateActiveFormats = () => {
    setActiveFormats({
      bold: document.queryCommandState('bold'),
      italic: document.queryCommandState('italic'),
      underline: document.queryCommandState('underline'),
      insertUnorderedList: document.queryCommandState('insertUnorderedList'),
      insertOrderedList: document.queryCommandState('insertOrderedList'),
      justifyLeft: document.queryCommandState('justifyLeft'),
      justifyCenter: document.queryCommandState('justifyCenter'),
      justifyRight: document.queryCommandState('justifyRight'),
    });
  };

  const handleInput = () => {
    updateActiveFormats();
    if (onChange) onChange(editorRef?.current?.innerHTML || '');
  };

  const handleInsertLink = () => {
    const url = window.prompt('Enter URL:', 'https://');
    if (url) execCmd('createLink', url);
  };

  const handleFontSize = (e) => {
    execCmd('fontSize', e?.target?.value);
  };

  const btnClass = (active) =>
    `p-1.5 rounded transition-colors ${active ? 'bg-primary text-primary-foreground' : 'hover:bg-muted text-foreground'}`;

  return (
    <div className="border border-border rounded-lg overflow-hidden">
      {/* Toolbar */}
      <div className="flex flex-wrap items-center gap-1 p-2 bg-muted/40 border-b border-border">
        <button type="button" onClick={() => execCmd('bold')} className={btnClass(activeFormats?.bold)} title="Bold">
          <Bold className="w-4 h-4" />
        </button>
        <button type="button" onClick={() => execCmd('italic')} className={btnClass(activeFormats?.italic)} title="Italic">
          <Italic className="w-4 h-4" />
        </button>
        <button type="button" onClick={() => execCmd('underline')} className={btnClass(activeFormats?.underline)} title="Underline">
          <Underline className="w-4 h-4" />
        </button>

        <div className="w-px h-5 bg-border mx-1" />

        <button type="button" onClick={() => execCmd('formatBlock', 'h2')} className="p-1.5 rounded hover:bg-muted text-foreground transition-colors text-xs font-bold" title="Heading 2">H2</button>
        <button type="button" onClick={() => execCmd('formatBlock', 'h3')} className="p-1.5 rounded hover:bg-muted text-foreground transition-colors text-xs font-bold" title="Heading 3">H3</button>
        <button type="button" onClick={() => execCmd('formatBlock', 'p')} className="p-1.5 rounded hover:bg-muted text-foreground transition-colors" title="Paragraph">
          <Type className="w-4 h-4" />
        </button>

        <div className="w-px h-5 bg-border mx-1" />

        <button type="button" onClick={() => execCmd('insertUnorderedList')} className={btnClass(activeFormats?.insertUnorderedList)} title="Bullet List">
          <List className="w-4 h-4" />
        </button>
        <button type="button" onClick={() => execCmd('insertOrderedList')} className={btnClass(activeFormats?.insertOrderedList)} title="Numbered List">
          <span className="text-xs font-bold w-4 h-4 flex items-center justify-center">1.</span>
        </button>

        <div className="w-px h-5 bg-border mx-1" />

        <button type="button" onClick={() => execCmd('justifyLeft')} className={btnClass(activeFormats?.justifyLeft)} title="Align Left">
          <AlignLeft className="w-4 h-4" />
        </button>
        <button type="button" onClick={() => execCmd('justifyCenter')} className={btnClass(activeFormats?.justifyCenter)} title="Align Center">
          <AlignCenter className="w-4 h-4" />
        </button>
        <button type="button" onClick={() => execCmd('justifyRight')} className={btnClass(activeFormats?.justifyRight)} title="Align Right">
          <AlignRight className="w-4 h-4" />
        </button>

        <div className="w-px h-5 bg-border mx-1" />

        <button type="button" onClick={handleInsertLink} className="p-1.5 rounded hover:bg-muted text-foreground transition-colors" title="Insert Link">
          <Link className="w-4 h-4" />
        </button>

        <div className="w-px h-5 bg-border mx-1" />

        <button type="button" onClick={() => execCmd('removeFormat')} className="p-1.5 rounded hover:bg-muted text-muted-foreground transition-colors text-xs" title="Clear Formatting">
          Clear
        </button>
      </div>
      {/* Editor area */}
      <div
        ref={editorRef}
        contentEditable
        suppressContentEditableWarning
        onInput={handleInput}
        onKeyUp={updateActiveFormats}
        onMouseUp={updateActiveFormats}
        className="min-h-[280px] p-4 text-foreground bg-background focus:outline-none prose prose-sm max-w-none"
        style={{ lineHeight: '1.7' }}
      />
    </div>
  );
};

const GROUP_OPTIONS = [
  { value: 'all', label: 'All Users', description: 'Send to every registered user' },
  { value: 'new', label: 'New Users (Last 30 Days)', description: 'Users who joined in the past month' },
  { value: 'inactive', label: 'Inactive Users', description: 'Users with no activity in 30+ days' },
  { value: 'active', label: 'Active Users', description: 'Users active in the last 30 days' },
  { value: 'selected', label: 'Selected Users', description: 'Only the users you have checked in the table' },
  { value: 'role_standard', label: 'Standard Role Users', description: 'All users with the Standard role' },
  { value: 'role_admin', label: 'Admin Role Users', description: 'All users with the Admin role' },
];

const OnboardingEmailModal = ({ isOpen, onClose, users = [], selectedUsers = [] }) => {
  const [step, setStep] = useState(1); // 1 = select recipients, 2 = compose email
  const [recipientMode, setRecipientMode] = useState('selected');
  const [subject, setSubject] = useState(DEFAULT_SUBJECT);
  const [htmlBody, setHtmlBody] = useState(DEFAULT_HTML);
  const [sending, setSending] = useState(false);
  const [result, setResult] = useState(null); // { type: 'success'|'error', text: '' }
  const [showPreview, setShowPreview] = useState(false);

  // Reset when opened
  useEffect(() => {
    if (isOpen) {
      setStep(1);
      setResult(null);
      setShowPreview(false);
      setRecipientMode(selectedUsers?.length > 0 ? 'selected' : 'all');
    }
  }, [isOpen]);

  if (!isOpen) return null;

  const getTargetUsers = () => {
    const now = new Date();
    const thirtyDaysAgo = new Date(now.getTime() - 30 * 24 * 60 * 60 * 1000);

    switch (recipientMode) {
      case 'all':
        return users;
      case 'new':
        return users?.filter(u => {
          if (!u?.joinDate) return false;
          const joined = new Date(u.joinDate);
          return joined >= thirtyDaysAgo;
        });
      case 'inactive':
        return users?.filter(u => u?.status === 'inactive');
      case 'active':
        return users?.filter(u => u?.status === 'active');
      case 'selected':
        return users?.filter(u => selectedUsers?.includes(u?.id));
      case 'role_standard':
        return users?.filter(u => u?.role === 'standard' || u?.role === 'user');
      case 'role_admin':
        return users?.filter(u => u?.role === 'admin');
      default:
        return [];
    }
  };

  const targetUsers = getTargetUsers();

  const handleSend = async () => {
    if (!subject?.trim()) {
      setResult({ type: 'error', text: 'Please enter an email subject.' });
      return;
    }
    if (!htmlBody?.trim() || htmlBody === '<br>') {
      setResult({ type: 'error', text: 'Please write an email body.' });
      return;
    }
    if (targetUsers?.length === 0) {
      setResult({ type: 'error', text: 'No recipients match the selected group.' });
      return;
    }

    setSending(true);
    setResult(null);

    try {
      const { supabase } = await import('../../../lib/supabase');

      // Wrap the user HTML in a branded email template
      const fullHtml = buildEmailTemplate(htmlBody);

      const recipients = targetUsers?.map(u => u?.email)?.filter(Boolean);

      const { error } = await supabase?.functions?.invoke('send-newsletter', {
        body: {
          subject: subject?.trim(),
          htmlBody: fullHtml,
          recipients,
        },
      });

      if (error) throw error;

      setResult({
        type: 'success',
        text: `Onboarding email sent successfully to ${recipients?.length} user${recipients?.length !== 1 ? 's' : ''}.`,
      });
    } catch (err) {
      console.error('Error sending onboarding email:', err);
      setResult({
        type: 'error',
        text: err?.message || 'Failed to send emails. Please try again.',
      });
    } finally {
      setSending(false);
    }
  };

  const buildEmailTemplate = (body) => {
    return `<!DOCTYPE html>
<html>
<head>
  <meta charset="utf-8">
  <meta name="viewport" content="width=device-width, initial-scale=1.0">
  <title>${subject}</title>
</head>
<body style="margin:0;padding:0;background:#f8f7f4;font-family:-apple-system,BlinkMacSystemFont,'Segoe UI',sans-serif;">
  <div style="max-width:600px;margin:40px auto;background:#fff;border-radius:16px;overflow:hidden;box-shadow:0 4px 24px rgba(0,0,0,0.08);">
    <div style="background:linear-gradient(135deg,#0f172a 0%,#1e1b4b 100%);padding:32px 24px;text-align:center;">
      <h1 style="color:#fbbf24;font-size:22px;font-weight:800;margin:0;letter-spacing:0.04em;text-transform:uppercase;">FORTIFIED MIND</h1>
      <p style="color:rgba(255,255,255,0.7);font-size:14px;margin:8px 0 0;">Scripture Memorization Platform</p>
    </div>
    <div style="padding:32px 24px;color:#374151;font-size:15px;line-height:1.7;">
      ${body}
    </div>
    <div style="background:#f9fafb;border-top:1px solid #e5e7eb;padding:16px 24px;text-align:center;">
      <p style="color:#9ca3af;font-size:12px;margin:0;">
        Fortified Mind · <a href="https://fortifiedmind.org" style="color:#d97706;">fortifiedmind.org</a>
      </p>
      <p style="color:#d1d5db;font-size:11px;margin:6px 0 0;">
        "None but those who have fortified the mind with the truths of the Bible will stand through the last great conflict."
      </p>
    </div>
  </div>
</body>
</html>`;
  };

  const selectedGroup = GROUP_OPTIONS?.find(g => g?.value === recipientMode);

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm">
      <div className="bg-card rounded-xl shadow-2xl border border-border w-full max-w-3xl max-h-[90vh] flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-border flex-shrink-0">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-success/10 rounded-lg flex items-center justify-center">
              <Mail className="w-5 h-5 text-success" />
            </div>
            <div>
              <h2 className="text-lg font-semibold text-foreground">Send Onboarding Email</h2>
              <p className="text-sm text-muted-foreground">
                {step === 1 ? 'Select recipients' : 'Compose your message'}
              </p>
            </div>
          </div>
          <button onClick={onClose} className="p-2 rounded-lg hover:bg-muted transition-colors">
            <X className="w-5 h-5 text-muted-foreground" />
          </button>
        </div>

        {/* Step indicator */}
        <div className="flex items-center gap-2 px-6 py-3 bg-muted/20 border-b border-border flex-shrink-0">
          <div className={`flex items-center gap-1.5 text-sm font-medium ${step === 1 ? 'text-primary' : 'text-muted-foreground'}`}>
            <div className={`w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold ${step === 1 ? 'bg-primary text-primary-foreground' : 'bg-success text-success-foreground'}`}>
              {step > 1 ? '✓' : '1'}
            </div>
            Recipients
          </div>
          <div className="flex-1 h-px bg-border" />
          <div className={`flex items-center gap-1.5 text-sm font-medium ${step === 2 ? 'text-primary' : 'text-muted-foreground'}`}>
            <div className={`w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold ${step === 2 ? 'bg-primary text-primary-foreground' : 'bg-muted text-muted-foreground'}`}>
              2
            </div>
            Compose
          </div>
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto p-6">
          {/* Step 1: Select Recipients */}
          {step === 1 && (
            <div className="space-y-4">
              <p className="text-sm text-muted-foreground">Choose who should receive this onboarding email:</p>

              <div className="grid grid-cols-1 gap-2">
                {GROUP_OPTIONS?.map(opt => {
                  const isDisabled = opt?.value === 'selected' && selectedUsers?.length === 0;
                  const count = opt?.value === 'selected' ? selectedUsers?.length : null;
                  return (
                    <button
                      key={opt?.value}
                      type="button"
                      disabled={isDisabled}
                      onClick={() => setRecipientMode(opt?.value)}
                      className={`flex items-center gap-3 p-3 rounded-lg border text-left transition-all ${
                        recipientMode === opt?.value
                          ? 'border-primary bg-primary/5'
                          : isDisabled
                          ? 'border-border opacity-40 cursor-not-allowed' :'border-border hover:border-primary/50 hover:bg-muted/30'
                      }`}
                    >
                      <div className={`w-4 h-4 rounded-full border-2 flex-shrink-0 flex items-center justify-center ${
                        recipientMode === opt?.value ? 'border-primary' : 'border-muted-foreground'
                      }`}>
                        {recipientMode === opt?.value && (
                          <div className="w-2 h-2 rounded-full bg-primary" />
                        )}
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2">
                          <span className="text-sm font-medium text-foreground">{opt?.label}</span>
                          {opt?.value === 'selected' && selectedUsers?.length > 0 && (
                            <span className="text-xs bg-primary/10 text-primary px-2 py-0.5 rounded-full">
                              {selectedUsers?.length} selected
                            </span>
                          )}
                        </div>
                        <p className="text-xs text-muted-foreground mt-0.5">{opt?.description}</p>
                      </div>
                    </button>
                  );
                })}
              </div>

              {/* Preview count */}
              <div className="flex items-center gap-2 p-3 bg-muted/30 rounded-lg border border-border">
                <Users className="w-4 h-4 text-muted-foreground flex-shrink-0" />
                <span className="text-sm text-foreground">
                  <strong>{targetUsers?.length}</strong> user{targetUsers?.length !== 1 ? 's' : ''} will receive this email
                  {selectedGroup && <span className="text-muted-foreground"> ({selectedGroup?.label})</span>}
                </span>
              </div>
            </div>
          )}

          {/* Step 2: Compose */}
          {step === 2 && (
            <div className="space-y-4">
              {/* Recipient summary */}
              <div className="flex items-center gap-2 p-3 bg-primary/5 rounded-lg border border-primary/20">
                <Users className="w-4 h-4 text-primary flex-shrink-0" />
                <span className="text-sm text-foreground">
                  Sending to <strong>{targetUsers?.length}</strong> user{targetUsers?.length !== 1 ? 's' : ''} — {selectedGroup?.label}
                </span>
              </div>

              {/* Subject */}
              <div>
                <label className="block text-sm font-medium text-foreground mb-1.5">Email Subject</label>
                <input
                  type="text"
                  value={subject}
                  onChange={(e) => setSubject(e?.target?.value)}
                  placeholder="Enter email subject..."
                  className="w-full px-3 py-2 border border-border rounded-lg bg-input text-foreground placeholder-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring"
                />
              </div>

              {/* Rich text editor */}
              <div>
                <div className="flex items-center justify-between mb-1.5">
                  <label className="block text-sm font-medium text-foreground">Email Body</label>
                  <button
                    type="button"
                    onClick={() => setShowPreview(!showPreview)}
                    className="text-xs text-primary hover:underline"
                  >
                    {showPreview ? 'Hide Preview' : 'Show Preview'}
                  </button>
                </div>

                {!showPreview ? (
                  <RichTextEditor value={htmlBody} onChange={setHtmlBody} />
                ) : (
                  <div className="border border-border rounded-lg overflow-hidden">
                    <div className="bg-muted/40 px-3 py-2 text-xs text-muted-foreground border-b border-border">
                      Email Preview (as recipients will see it)
                    </div>
                    <div
                      className="p-4 min-h-[280px] prose prose-sm max-w-none text-foreground"
                      dangerouslySetInnerHTML={{ __html: htmlBody }}
                    />
                  </div>
                )}
              </div>

              {/* Result message */}
              {result && (
                <div className={`flex items-start gap-2 p-3 rounded-lg border text-sm ${
                  result?.type === 'success' ?'bg-success/10 border-success/20 text-success' :'bg-destructive/10 border-destructive/20 text-destructive'
                }`}>
                  {result?.type === 'success'
                    ? <CheckCircle className="w-4 h-4 flex-shrink-0 mt-0.5" />
                    : <AlertCircle className="w-4 h-4 flex-shrink-0 mt-0.5" />}
                  {result?.text}
                </div>
              )}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-between p-6 border-t border-border flex-shrink-0 bg-muted/10">
          <div>
            {step === 2 && (
              <button
                type="button"
                onClick={() => { setStep(1); setResult(null); }}
                className="text-sm text-muted-foreground hover:text-foreground transition-colors"
              >
                ← Back to Recipients
              </button>
            )}
          </div>
          <div className="flex items-center gap-3">
            <Button variant="outline" onClick={onClose} disabled={sending}>
              {result?.type === 'success' ? 'Close' : 'Cancel'}
            </Button>
            {step === 1 ? (
              <Button
                variant="default"
                onClick={() => setStep(2)}
                disabled={targetUsers?.length === 0}
              >
                Next: Compose →
              </Button>
            ) : (
              <Button
                variant="default"
                onClick={handleSend}
                disabled={sending || result?.type === 'success'}
                className="bg-success hover:bg-success/90 text-success-foreground"
              >
                {sending ? (
                  <><RefreshCw className="w-4 h-4 mr-2 animate-spin" />Sending...</>
                ) : (
                  <><Send className="w-4 h-4 mr-2" />Send to {targetUsers?.length} User{targetUsers?.length !== 1 ? 's' : ''}</>
                )}
              </Button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default OnboardingEmailModal;
